import React, { useState } from 'react';
import { ChevronLeft, Bookmark, Clock, TrendingUp } from 'lucide-react';

const EssayTopicsMinimalSwiss = () => {
  const [selectedType, setSelectedType] = useState('all');
  const [expandedTopic, setExpandedTopic] = useState(1);

  const topics = [
    {
      id: 1,
      number: 1,
      title: "The Funding Restructure Victory",
      subtitle: "Your student government funding restructure perfectly captures your strategic thinking, coalition-building skills, and commitment to systemic fairness while showing real political risk-taking for principle",
      badges: ['Top Pick', 'Strongest Evidence'],
      accentColor: 'gold',
      uniqueness: 5,
      evidence: 5,
      difficulty: 2,
      timeEstimate: "4-5 hours",
      whyThisWins: "This story proves you can take meaningful political risks for principle while demonstrating tactical sophistication to actually win - most applicants have either principles or tactics, not both"
    },
    {
      id: 2,
      number: 2,
      title: "The Distance Between People - exploring leadership as translation",
      subtitle: "Shows your sophisticated understanding of how to bridge different perspectives and create understanding without losing nuance",
      badges: ['Most Philosophical'],
      accentColor: 'indigo',
      uniqueness: 4,
      evidence: 2,
      difficulty: 4,
      timeEstimate: "4-5 hours",
      whyThisWins: "Most leadership essays focus on commanding or inspiring - yours focuses on connecting, which is far more rare and valuable"
    },
    {
      id: 3,
      number: 3,
      title: "Hallway Politics - lessons from Congressional Page experience",
      subtitle: "Demonstrates your insider knowledge of how real influence flows and how it changed your approach to leadership",
      badges: ['From Assessment'],
      accentColor: 'teal',
      uniqueness: 4,
      evidence: 2,
      difficulty: 4,
      timeEstimate: "5-6 hours",
      whyThisWins: "Gives you credibility and perspective that 99% of applicants simply cannot match"
    },
    {
      id: 4,
      number: 4,
      title: "The Pivot - voter registration strategy adaptation",
      subtitle: "Shows your ability to read a situation and adapt strategy in real-time rather than stubbornly sticking to a failing plan",
      badges: [],
      accentColor: 'coral',
      uniqueness: 3,
      evidence: 2,
      difficulty: 2,
      timeEstimate: "3-4 hours",
      whyThisWins: "Demonstrates practical strategic thinking and humility to change course when needed"
    }
  ];

  const topicTypes = [
    { id: 'all', label: 'All Topics', icon: null },
    { id: 'top', label: 'Top Pick', color: 'gold' },
    { id: 'featured', label: 'Featured Topics', color: 'indigo' },
    { id: 'assessment', label: 'From Assessment', color: 'teal' }
  ];

  const colorClasses = {
    gold: 'bg-[#ffd54f]',
    teal: 'bg-[#26a69a]',
    indigo: 'bg-[#7e57e2]',
    coral: 'bg-[#ff6b6b]',
    azure: 'bg-[#4290f5]'
  };

  const renderDots = (count, max = 5, color = null) => {
    return (
      <div className="flex gap-1">
        {[...Array(max)].map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${
              i < count 
                ? (color ? colorClasses[color] : 'bg-slate-900')
                : 'bg-stone-200'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <button className="flex items-center gap-2 text-stone-600 text-sm mb-4 hover:text-slate-900 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Ideas Hub
          </button>
          
          <div className="flex items-start gap-3 mb-2">
            <div className="w-1 h-8 bg-slate-900 rounded-full" />
            <h1 className="text-2xl font-bold text-slate-900">Your Strategic Essay Topics</h1>
          </div>
          
          <p className="text-stone-600 text-sm ml-5">
            Your strategic assessment generated the following essay topics based on what you've already shared. 
            Choose the one that resonates most with you to develop into a full essay outline.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Topic Type Filters */}
        <div className="mb-8">
          <div className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">
            Topic Types
          </div>
          <div className="flex gap-2">
            {topicTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                  selectedType === type.id
                    ? 'border-2 border-slate-900 bg-white text-slate-900 font-medium'
                    : 'border border-stone-300 bg-white text-stone-600 hover:border-stone-400'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Comparison Table */}
        <div className="mb-8 bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
          <div className="px-6 py-4 bg-slate-900/5 border-b border-slate-900/10">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
              Quick Comparison
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Uniqueness
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Evidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Difficulty
                  </th>
                </tr>
              </thead>
              <tbody>
                {topics.map((topic, idx) => (
                  <tr 
                    key={topic.id}
                    className={`border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors ${
                      idx === 0 ? 'bg-stone-50/50' : ''
                    }`}
                    onClick={() => setExpandedTopic(topic.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="text-sm font-medium text-stone-400 pt-0.5">
                          {topic.number}.
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900 mb-1">
                            {topic.title}
                          </div>
                          {topic.badges.length > 0 && (
                            <div className="flex gap-1.5 mt-1">
                              {topic.badges.map((badge, i) => (
                                <div
                                  key={i}
                                  className="text-xs px-2 py-0.5 bg-white border border-slate-900 rounded"
                                >
                                  {badge}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {renderDots(topic.uniqueness, 5, topic.accentColor)}
                    </td>
                    <td className="px-6 py-4">
                      {renderDots(topic.evidence, 5, topic.accentColor)}
                    </td>
                    <td className="px-6 py-4">
                      {renderDots(topic.difficulty, 5, topic.accentColor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expanded Topic Details */}
        {topics.map(topic => expandedTopic === topic.id && (
          <div key={topic.id} className="bg-white border-2 border-slate-900 rounded-xl p-6 mb-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 pb-4 border-b border-stone-200">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-sm font-medium text-stone-400">
                    {topic.number}.
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {topic.title}
                  </h3>
                </div>
                
                {topic.badges.length > 0 && (
                  <div className="flex gap-2 mb-3">
                    {topic.badges.map((badge, i) => (
                      <div
                        key={i}
                        className="text-xs px-3 py-1 bg-white border border-slate-900 rounded-lg font-medium"
                      >
                        {badge}
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-sm text-stone-700 leading-relaxed">
                  {topic.subtitle}
                </p>
              </div>
              
              <button className="p-2 hover:bg-stone-50 rounded-lg transition-colors ml-4">
                <Bookmark className="w-5 h-5 text-stone-400" />
              </button>
            </div>

            {/* Why This Wins */}
            <div className="mb-6">
              <div className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">
                Why This Wins
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">
                {topic.whyThisWins}
              </p>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-3 gap-6 mb-6 pb-6 border-b border-stone-200">
              <div>
                <div className="text-xs text-stone-500 mb-2">Uniqueness</div>
                {renderDots(topic.uniqueness, 5, topic.accentColor)}
                <div className="text-xs text-stone-500 mt-1">
                  {topic.uniqueness === 5 ? 'Very High' : topic.uniqueness === 4 ? 'High' : 'Medium'}
                </div>
              </div>
              <div>
                <div className="text-xs text-stone-500 mb-2">Evidence</div>
                {renderDots(topic.evidence, 5, topic.accentColor)}
                <div className="text-xs text-stone-500 mt-1">
                  {topic.evidence === 5 ? 'Concrete' : topic.evidence === 2 ? 'Limited' : 'Moderate'}
                </div>
              </div>
              <div>
                <div className="text-xs text-stone-500 mb-2">Difficulty</div>
                {renderDots(topic.difficulty, 5, topic.accentColor)}
                <div className="text-xs text-stone-500 mt-1">
                  {topic.difficulty === 2 ? 'Easy' : topic.difficulty === 4 ? 'Challenging' : 'Medium'}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <Clock className="w-4 h-4" />
                {topic.timeEstimate}
              </div>
              
              <button className="px-6 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
                Develop this topic →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EssayTopicsMinimalSwiss;